"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnalyticsProvider = exports.inspectlet = exports.mixpanel = exports.Analytics = exports.useIntercom = exports.useAnalytics = void 0;
const inspectlet_es_1 = __importStar(require("inspectlet-es"));
const mixpanel_browser_1 = __importDefault(require("mixpanel-browser"));
const React = __importStar(require("react"));
const react_use_intercom_1 = require("react-use-intercom");
Object.defineProperty(exports, "useIntercom", { enumerable: true, get: function () { return react_use_intercom_1.useIntercom; } });
const context_1 = require("./components/context");
Object.defineProperty(exports, "Analytics", { enumerable: true, get: function () { return context_1.Analytics; } });
Object.defineProperty(exports, "useAnalytics", { enumerable: true, get: function () { return context_1.useAnalytics; } });
const page_reporter_1 = require("./components/page_reporter");
const session_reporter_1 = require("./components/session_reporter");
exports.mixpanel = mixpanel_browser_1.default;
exports.inspectlet = inspectlet_es_1.default;
const AnalyticsProviderInner = ({ userId: propsUserId, routes, isTrackingAllowed, initialProperties = {}, mixpanelToken, mixpanelProxyUrl, inspectletWid, intercomOptions: { appId, shouldTrackCustomEvents } = {}, children, }) => {
    const properties = React.useRef(initialProperties);
    const [isReady, setIsReady] = React.useState(false);
    const [userId, setUserId] = React.useState(propsUserId);
    const shouldTrackMixpanel = mixpanelToken !== undefined;
    const shouldTrackInspectlet = inspectletWid !== undefined;
    const shouldTrackIntercom = appId !== undefined;
    const shouldTrack = isTrackingAllowed && (shouldTrackMixpanel || shouldTrackInspectlet || shouldTrackIntercom);
    const mixpanel = shouldTrackMixpanel ? mixpanel_browser_1.default : undefined;
    const inspectlet = shouldTrackInspectlet ? inspectlet_es_1.default : undefined;
    // We use this indirection to avoid calling functions on the Intercom reference if we don't
    // plan to initialise/boot it
    const intercomRef = (0, react_use_intercom_1.useIntercom)();
    const intercom = shouldTrackIntercom ? intercomRef : undefined;
    React.useEffect(() => {
        if (!shouldTrack) {
            return;
        }
        if (mixpanelToken !== undefined) {
            mixpanel?.init(mixpanelToken, {
                api_host: mixpanelProxyUrl !== undefined ? new URL(mixpanelProxyUrl, window.location.href).href : undefined,
                ip: false,
            });
        }
        if (inspectletWid !== undefined) {
            (0, inspectlet_es_1.install)(inspectletWid);
        }
        if (appId !== undefined) {
            intercom?.boot({ hideDefaultLauncher: true });
        }
        setIsReady(true);
    }, [mixpanelToken, mixpanelProxyUrl, inspectletWid, appId, shouldTrack, setIsReady, intercom]);
    React.useEffect(() => {
        if (!shouldTrack || !isReady) {
            return;
        }
        // If we allowed this, the sequence would end up being identify - reset - identify
        // which Mixpanel would see as us having a weird anonymous user for the first part
        // meaning any events that got triggered before we called identify with the proper user
        // ID would incorrectly be filed with some anonymous user
        // As such, in case we have no user ID, we do not want to proceed, nor do we want to reset
        // when this effect triggers again
        if (userId === undefined) {
            return;
        }
        mixpanel?.identify(userId);
        inspectlet?.(['identify', userId]);
        intercom?.update({ userId });
        return () => {
            mixpanel?.reset();
            inspectlet?.(['identify', 'N/A']);
            intercom?.update({ userId: undefined });
        };
    }, [isReady, shouldTrack, userId, mixpanel, inspectlet, intercom]);
    const analytics = React.useMemo(() => {
        if (!shouldTrack || !isReady) {
            return undefined;
        }
        return {
            track: (event, metadata) => {
                const data = { ...properties.current, ...metadata };
                mixpanel?.track(event, data);
                shouldTrackCustomEvents && intercom?.trackEvent(event, data);
                inspectlet?.(['tagSession', event, properties.current, metadata]);
            },
            page: (name, metadata) => {
                const data = { ...properties.current, ...metadata, name };
                mixpanel?.track('Page loaded', data);
                intercom?.trackEvent('Page loaded', data);
                inspectlet?.(['virtualPage']);
            },
            identify: (userId) => {
                setUserId(userId);
            },
            clearProperties: () => {
                properties.current = {};
            },
            setProperties: (newProperties) => {
                properties.current = { ...properties.current, ...newProperties };
            },
            setIntercomProperties: (newProperties) => {
                intercom?.update(newProperties);
            },
        };
    }, [shouldTrack, isReady, mixpanelToken, mixpanel, inspectlet, intercom]);
    return (React.createElement(context_1.Analytics.Provider, { value: analytics },
        React.createElement(session_reporter_1.AnalyticsSessionReporter, null),
        React.createElement(page_reporter_1.AnalyticsPageReporter, { routes: routes }),
        children));
};
const AnalyticsProvider = ({ intercomOptions, isTrackingAllowed, ...rest }) => {
    return (React.createElement(react_use_intercom_1.IntercomProvider, { appId: intercomOptions?.appId ?? 'fake-intercom-app-id', shouldInitialize: isTrackingAllowed && intercomOptions?.appId !== undefined },
        React.createElement(AnalyticsProviderInner, { intercomOptions: intercomOptions, isTrackingAllowed: isTrackingAllowed, ...rest })));
};
exports.AnalyticsProvider = AnalyticsProvider;
//# sourceMappingURL=index.js.map